import type { airportResponseType, JourneyDetailsResponse, OrderDetailsResponse } from "src/Modules/Flights/constants";
import { FlightSearchRequestType } from "src/Modules/Flights/constants/SearchPage";
import { FlightResponseType } from "src/Modules/Flights/constants";
import BaseApi from "src/store/BaseApi";
const PrivateAPIWithTags = BaseApi.enhanceEndpoints({
  addTagTypes: [""],
});

type ResponseType<T> = {
  message: string;
  errorCode: 0;
  referenceId: string;
  result: T;
  data: T;
};

type InitiateOrderResponse = {
  orderId: string;
  clientSecret?: string;
  changeInItinerary:{
    isPriceChanged?:boolean;
    isArrivalTimeChanged?:boolean;
    isDepartureTimeChanged?:boolean;
  },
  ssrDenied?:boolean;
};

export const FlightsApi = PrivateAPIWithTags.injectEndpoints({
  endpoints: (builder) => ({
    getAirPorts: builder.query<{ airports: airportResponseType[]; total: number }, { query: string; limit: number }>({
      query: ({ query, limit }) => {
        return {
          url: `Travel/airports/search`,
          method: "POST",
          body: { query, limit },
        };
      },
      transformResponse: (response: ResponseType<{ airports: airportResponseType[]; total: number }>) => {
        if (response?.result?.airports?.length === 0) throw new Error("No Airports");
        return response.result;
      },
      extraOptions: {
        notifyOnFailure: true,
      },
    }),
    getFlights: builder.query<FlightResponseType, { request: FlightSearchRequestType; time: string }>({
      query: (body) => ({
        url: `travel/flights/search`,
        method: "POST",
        body: body.request,
      }),
      transformResponse: (response: ResponseType<FlightResponseType>) => {
        return response.result;
      },
      keepUnusedDataFor: Number.MAX_SAFE_INTEGER,
    }),
    getFlightDetails: builder.query<JourneyDetailsResponse, any>({
      query: (journeyId: string) => ({
        url: `travel/flights/details`,
        method: "POST",
        body: {
          journeyId,
        },
      }),
      keepUnusedDataFor: Number.MAX_SAFE_INTEGER,
      transformResponse: (response: { result: JourneyDetailsResponse }) => {
        return response?.result;
      },
      extraOptions: {
        notifyOnFailure: true,
      },
    }),
    ticket: builder.query<any, any>({
      query: (body) => ({
        url: "/travel/Flights/ticket",
        body,
        method: "POST",
      }),
      transformResponse: (response: any) => {
        return response?.data;
      },
    }),
    initiateOrder: builder.mutation<InitiateOrderResponse, any>({
      query: (body) => ({
        url: "travel/Flights/initiate",
        body,
        method: "POST",
      }),
      transformResponse: (response: any) => {
        return response?.result;
      },
    }),
     getOrderDetails: builder.query<OrderDetailsResponse, any>({
      query: (body) => ({
        url: "travel/Flights/OrderDetail",
        body,
        method: "POST",
      }),
      transformResponse: (response: ResponseType<OrderDetailsResponse>) => {
        return response?.result;
      },
    }),
  }),
});

type FlightApiEndpoints = keyof typeof FlightsApi.endpoints;
type FlightDetailsHookReturnType = ReturnType<typeof FlightsApi.useGetFlightDetailsQuery>;
export { type FlightApiEndpoints, type FlightDetailsHookReturnType };
export const {
  useLazyGetAirPortsQuery,
  useGetFlightsQuery,
  useGetFlightDetailsQuery,
  useInitiateOrderMutation,
  useGetOrderDetailsQuery,
  useLazyTicketQuery,
  useLazyGetOrderDetailsQuery,
} = FlightsApi;
